/* Other imports. */
import { isDevelopmentEnvironment } from "./helpers";

/**
 * Contains URLs for websites used throughout the framework.
 */
export const websiteURLs = {
  panmacmillan: {
    development: "http://localhost:8000/",
    production: "https://www.panmacmillan.com/",
    azure: "https://panmacmillan.azureedge.net/",
  },
  twitter: {
    share: "https://twitter.com/intent/tweet?source=webclient&text=",
  },
  facebook: {
    share: "https://www.facebook.com/sharer/sharer.php?u=",
  },
};

/**
 * The URL for the current environment (development or production).
 */
export const environmentURL = isDevelopmentEnvironment()
  ? websiteURLs.panmacmillan.development
  : websiteURLs.panmacmillan.production;

/**
 * Contains URLs for services used throughout the framework.
 */
export const serviceURLs = {
  kentico: {
    assets: "https://assets-eu-01.kc-usercontent.com:443",
    previewAssets: "https://preview-assets-eu-01.kc-usercontent.com:443",
    assetsUS: "https://assets-us-01.kc-usercontent.com:443",
    previewAssetsUS: "https://preview-assets-us-01.kc-usercontent.com:443",
  },
  imagekit: {
    main: "https://ik.imagekit.io/panmac/",
  },
};

/**
 * Contains data specific to Kentico Kontent.
 */
export const kontent = {
  components: {
    types: {
      anchorlinks: "anchor_links",
      banner: "banner",
      bannercarousel: "banner_carousel",
      bannercollection: "banner_collection",
      bannerheader: "header",
      button: "button_link",
      bookSpotlight: "book_spotlight",
      bookSpotlightCollection: "book_spotlight_collection",
      columns: "columns",
      contact: "contact",
      contactsGroup: "contacts_group",
      contributorSpotlight: "contributor_spotlight",
      contributorSpotlightCollection: "contributor_spotlight_collection",
      dontmiss: "don_t_miss",
      download: "downloadable_resource",
      embed: "embed___iframe",
      event: "event",
      eventCollection: "event_collection",
      featuredContent: "external_featured_content",
      featuredArticles: "featured_articles",
      featuredResources: "featured_resources",
      faq: "faq_list",
      header: "header",
      highlightColour: "highlight_colour",
      image: "inline_image",
      instagramEmbed: "instagram_embed__",
      isbnList: "isbn_list_",
      newsletterForm: "newsletter_form",
      productCollection: "product_collection",
      quote: "quote",
      recipe: "recipe_article",
      reusableRichText: "reusable_rich_content",
      sectionBreak: "section_break",
      soundcloudEmbed: "soundcloud_embed__",
      twitterTimeline: "twitter_embedded_timeline",
      youtubeCollection: "youtube_collection",
      youtubeEmbed: "youtube_embed",
    },
  },
};

/**
 * Contains objects relating to Article Categories.
 */

export const articleCategories = {
  authorCorner: {
    codename: "author_corner",
    slug: "author-corner",
  },
  booksForChildren: {
    codename: "books_for_children",
    slug: "books-for-children",
  },
  classics: {
    codename: "classics",
    slug: "classics",
  },
  crimeThrillers: {
    codename: "crime___thrillers",
    slug: "crime-thriller",
  },
  fiction: {
    codename: "fiction",
    slug: "fiction",
  },
  general: {
    codename: "general",
    slug: "general",
  },
  history: {
    codename: "history",
    slug: "history",
  },
  lifestyleAndWellbeing: {
    codename: "lifestyle___wellbeing",
    slug: "lifestyle-wellbeing",
  },
  literary: {
    codename: "literary",
    slug: "literary",
  },
  news: {
    codename: "news",
    slug: "news",
  },
  picador: {
    codename: "picador",
    slug: "picador",
  },
  recipes: {
    codename: "recipes",
    slug: "recipes",
  },
  scienceFictionAndFantasy: {
    codename: "science_fiction___fantasy",
    slug: "science-fiction-and-fantasy",
  },
  tradeNews: {
    codename: "trade_news",
    slug: "trade-news",
  },
  youngAdult: {
    codename: "young_adult",
    slug: "young-adult",
  },
};

export const nonPanMacFormat = "Unknown";

export const multimediaFormats = {
  AA: {
    name: "Audio",
    schema: "https://schema.org/AudiobookFormat",
    code: "AA"
  },
  AB: {
    name: "Audio Cassette",
    schema: "https://schema.org/AudiobookFormat",
    code: "AB"
  },
  AC: {
    name: "CD Audio",
    schema: "https://schema.org/AudiobookFormat",
    code: "AC"
  },
  AJ: {
    name: "Audiobook",
    schema: "https://schema.org/AudiobookFormat",
    code: "AJ"
  },
  AN: {
    name: "Audiobook",
    schema: "https://schema.org/AudiobookFormat",
    code: "AN"
  },
  AO: {
    name: "Audiobook",
    schema: "https://schema.org/AudiobookFormat",
    code: "AO"
  },
  AZ: {
    name: "Other audio format",
    schema: "https://schema.org/AudiobookFormat",
    code: "AZ"
  },
  BA: {
    name: "Book",
    schema: "https://schema.org/bookFormat",
    code: "BA"
  },
  BB: {
    name: "Hardback",
    schema: "https://schema.org/Hardcover",
    code: "BB"
  },
  BC: {
    name: "Paperback",
    schema: "https://schema.org/Paperback",
    code: "BC"
  },
  BD: {
    name: "Loose-leaf",
    schema: "",
    code: "BD"
  },
  BH: {
    name: "Board book",
    schema: "",
    code: "BH"
  },
  BI: {
    name: "Rag book",
    schema: "",
    code: "BI"
  },
  BJ: {
    name: "Bath book",
    schema: "",
    code: "BJ"
  },
  BK: {
    name: "Novelty book",
    schema: "",
    code: "BK"
  },
  BL: {
    name: "Slide bound",
    schema: "",
    code: "BL"
  },
  BM: {
    name: "Big book",
    schema: "",
    code: "BM"
  },
  BN: {
    name: "Part-work (fascículo)",
    schema: "",
    code: "BN"
  },
  BO: {
    name: "Fold-out book or chart",
    schema: "",
    code: "BO"
  },
  BP: {
    name: "Foam book",
    schema: "",
    code: "BP"
  },
  BZ: {
    name: "Other book format",
    schema: "",
    code: "BZ"
  },
  DA: {
    name: "Digital (on physical carrier)",
    schema: "https://schema.org/EBook",
    code: "DA"
  },
  DI: {
    name: "DVD-ROM",
    schema: "",
    code: "DI"
  },
  EA: {
    name: "Digital (delivered electronically)",
    schema: "https://schema.org/EBook",
    code: "EA"
  },
  EB: {
    name: "Digital download and online",
    schema: "https://schema.org/EBook",
    code: "EB"
  },
  EC: {
    name: "Digital online",
    schema: "https://schema.org/EBook",
    code: "EC"
  },
  ED: {
    name: "Ebook",
    schema: "https://schema.org/EBook",
    code: "ED"
  },
  PA: {
    name: "Miscellaneous print",
    schema: "",
    code: "PA"
  },
  PB: {
    name: "Address book",
    schema: "",
    code: "PB"
  },
  PC: {
    name: "Calendar",
    schema: "",
    code: "PC"
  },
  PD: {
    name: "Cards",
    schema: "",
    code: "PD"
  },
  PF: {
    name: "Diary",
    schema: "",
    code: "PF"
  },
  PJ: {
    name: "Postcard book or pack",
    schema: "",
    code: "PJ"
  },
  PK: {
    name: "Poster",
    schema: "",
    code: "PK"
  },
  PL: {
    name: "Record book",
    schema: "",
    code: "PL"
  },
  PR: {
    name: "Notebook / blank book",
    schema: "",
    code: "PR"
  },
  PS: {
    name: "Organizer",
    schema: "",
    code: "PS"
  },
  PZ: {
    name: "Other printed item",
    schema: "",
    code: "PZ"
  },
  SA: {
    name: "Multiple-component retail product",
    schema: "",
    code: "SA"
  },
  SB: {
    name: "Box set",
    schema: "",
    code: "SB"
  },
  SC: {
    name: "Slip case",
    schema: "",
    code: "SC"
  },
  SD: {
    name: "Multiple-component retail product, shrink-wrapped",
    schema: "",
    code: "SD"
  },
  SE: {
    name: "Multiple-component retail product, loose",
    schema: "",
    code: "SE"
  },
  SF: {
    name: "Multiple-component retail product, part(s) enclosed",
    schema: "",
    code: "SF"
  },
  ZA: {
    name: "General merchandise",
    schema: "",
    code: "ZA"
  },
  ZB: {
    name: "Doll",
    schema: "",
    code: "ZB"
  },
  ZC: {
    name: "Soft toy",
    schema: "",
    code: "ZC"
  },
  ZD: {
    name: "Toy",
    schema: "",
    code: "ZD"
  },
  ZE: {
    name: "Game",
    schema: "",
    code: "ZE"
  },
  ZZ: {
    name: "Other merchandise",
    schema: "",
    code: "ZZ"
  },
};
